// lib/auth/login-prompt-snooze.ts
//
// Throttle policy for the global guest "join Kraahl" prompt.
//
// Why this file exists: the prompt's only memory used to be `dismissedPath` —
// React state holding the single pathname the guest dismissed on. Any client
// navigation carried a different pathname, and a reload dropped the state
// entirely, so a guest browsing the feed got the modal on essentially every
// step. A dismissal now buys silence for a fixed window across every path, tab
// and reload: the timestamp lives in localStorage and the decision below is a
// pure function of (stored timestamp, now).
//
// Decision + parsing are pure so they unit-test without React/DOM; the two
// storage functions at the bottom are the only impure part.
//
// @see components/auth/global-login-prompt.tsx — the only consumer
// @see tests/unit/login-prompt-snooze.test.ts

import { unreachable } from "@/lib/type-helpers"

/** How long one dismissal keeps the prompt quiet. */
export const LOGIN_PROMPT_SNOOZE_MS = 5 * 60 * 1000

/** Settle delay after a navigation before the prompt may open. */
export const LOGIN_PROMPT_OPEN_DELAY_MS = 1500

/** Bump the `.v1` suffix to invalidate every stored snooze after a policy change. */
export const LOGIN_PROMPT_SNOOZE_KEY = "kraahl.loginPrompt.snoozedAt.v1"

export type LoginPromptSnoozeDecision =
  /** No live snooze — the prompt may open after the usual settle delay. */
  | { kind: "arm" }
  /** Dismissed recently — stay silent for `remainingMs` more. */
  | { kind: "snoozed"; remainingMs: number }

export type LoginPromptSnoozeInput = {
  /** Epoch ms of the last dismissal, or null when nothing is remembered. */
  snoozedAt: number | null
  /** Epoch ms now — passed in rather than read, which keeps this pure. */
  now: number
  windowMs?: number
}

export function decideLoginPromptSnooze({
  snoozedAt,
  now,
  windowMs = LOGIN_PROMPT_SNOOZE_MS,
}: LoginPromptSnoozeInput): LoginPromptSnoozeDecision {
  if (snoozedAt === null) return { kind: "arm" }
  const elapsed = now - snoozedAt
  // Negative elapsed = the stored stamp is in the future (clock moved back, or
  // a hand-edited value). Fail open to "arm": a far-future stamp would
  // otherwise mute the prompt until the clock caught up, i.e. forever.
  if (elapsed < 0) return { kind: "arm" }
  if (elapsed >= windowMs) return { kind: "arm" }
  return { kind: "snoozed", remainingMs: windowMs - elapsed }
}

/**
 * Single timer delay the component needs: settle delay when armed, rest of the
 * snooze window plus settle delay when still snoozed. Folding both branches
 * into one number means the caller keeps ONE `setTimeout` and the prompt
 * re-appears once the window lapses even if the guest never navigates.
 */
export function deriveLoginPromptOpenDelayMs(
  input: LoginPromptSnoozeInput & { openDelayMs?: number },
): number {
  const openDelayMs = input.openDelayMs ?? LOGIN_PROMPT_OPEN_DELAY_MS
  const decision = decideLoginPromptSnooze(input)
  switch (decision.kind) {
    case "arm":
      return openDelayMs
    case "snoozed":
      return decision.remainingMs + openDelayMs
    default:
      return unreachable(decision)
  }
}

/**
 * Total function over whatever is in storage: missing, non-numeric, zero,
 * negative and `Infinity` all read as "nothing remembered".
 */
export function parseSnoozedAt(raw: string | null): number | null {
  if (raw === null) return null
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return parsed
}

// --- browser adapter ------------------------------------------------------
// `localStorage` is absent during SSR and throws in Safari private mode / on
// exhausted quota. Both degrade to the pre-snooze behaviour, never to a crash.

export function readLoginPromptSnoozedAt(): number | null {
  if (typeof window === "undefined") return null
  try {
    return parseSnoozedAt(window.localStorage.getItem(LOGIN_PROMPT_SNOOZE_KEY))
  } catch {
    return null
  }
}

export function writeLoginPromptSnoozedAt(at: number): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(LOGIN_PROMPT_SNOOZE_KEY, String(at))
  } catch {
    // Snooze degrades to this tab's in-memory state; not worth surfacing.
  }
}
